export type LegalInline =
  | { type: 'text'; value: string }
  | { type: 'strong'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; value: string; href: string }
  | { type: 'break' };

export type LegalFlow =
  | { type: 'p'; children: LegalInline[] }
  | { type: 'ul'; items: LegalInline[][] }
  | { type: 'table'; headers: string[]; rows: string[][] };

export type LegalSection = {
  heading: LegalInline[];
  blocks: LegalFlow[];
};

export type LegalDocument = {
  title: string;
  updatedText: string;
  updatedIso: string;
  intro: LegalFlow[];
  sections: LegalSection[];
};

const MONTHS: Record<string, string> = {
  January: '01',
  February: '02',
  March: '03',
  April: '04',
  May: '05',
  June: '06',
  July: '07',
  August: '08',
  September: '09',
  October: '10',
  November: '11',
  December: '12',
};

const EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

export function legalHref(href: string): string {
  if (href === 'privacy-policy.md') return '/privacy';
  if (href === 'terms-of-service.md') return '/terms';
  return href;
}

export function displayTitle(raw: string): string {
  return raw.replace(/^Exis\s+/, '');
}

export function parseUpdatedLine(
  line: string,
): { text: string; iso: string } | undefined {
  const match = /^\*\*Last updated:\s*(.+)\*\*$/.exec(line.trim());
  if (!match) return undefined;
  const text = match[1].trim();
  const date = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(text);
  if (!date) return { text, iso: text };
  const month = MONTHS[date[2]];
  if (!month) return { text, iso: text };
  return { text, iso: `${date[3]}-${month}-${date[1].padStart(2, '0')}` };
}

export function parseInline(source: string): LegalInline[] {
  const out: LegalInline[] = [];
  let i = 0;
  while (i < source.length) {
    const taken = takeInline(source, i);
    out.push(taken.node);
    i = taken.next;
  }
  return out;
}

function takeInline(
  source: string,
  i: number,
): { node: LegalInline; next: number } {
  const strong = takeWrapped(source, i, '**', 'strong');
  if (strong) return strong;
  const code = takeWrapped(source, i, '`', 'code');
  if (code) return code;
  const link = takeLink(source, i);
  if (link) return link;
  const email = takeEmail(source, i);
  if (email) return email;
  return takeText(source, i);
}

function takeWrapped(
  source: string,
  i: number,
  delim: string,
  type: 'strong' | 'code',
): { node: LegalInline; next: number } | undefined {
  if (!source.startsWith(delim, i)) return undefined;
  const end = source.indexOf(delim, i + delim.length);
  if (end === -1) return undefined;
  return {
    node: { type, value: source.slice(i + delim.length, end) },
    next: end + delim.length,
  };
}

function takeLink(
  source: string,
  i: number,
): { node: LegalInline; next: number } | undefined {
  if (source[i] !== '[') return undefined;
  const close = source.indexOf('](', i);
  if (close === -1) return undefined;
  const end = source.indexOf(')', close + 2);
  if (end === -1) return undefined;
  return {
    node: {
      type: 'link',
      value: source.slice(i + 1, close),
      href: legalHref(source.slice(close + 2, end)),
    },
    next: end + 1,
  };
}

function takeEmail(
  source: string,
  i: number,
): { node: LegalInline; next: number } | undefined {
  const match = EMAIL.exec(source.slice(i));
  if (!match) return undefined;
  return {
    node: { type: 'link', value: match[0], href: `mailto:${match[0]}` },
    next: i + match[0].length,
  };
}

function takeText(
  source: string,
  i: number,
): { node: LegalInline; next: number } {
  const rest = source.slice(i);
  const next = rest.search(
    /(\*\*|`|\[|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,})/,
  );
  const take = next === -1 ? rest.length : next === 0 ? 1 : next;
  return {
    node: { type: 'text', value: source.slice(i, i + take) },
    next: i + take,
  };
}

function isBlank(line: string): boolean {
  return line.trim() === '';
}

function isHeading(line: string): boolean {
  return line.startsWith('#');
}

function isTableRow(line: string): boolean {
  return line.startsWith('|');
}

function isListItem(line: string): boolean {
  return line.startsWith('- ');
}

function skipBlank(lines: string[], i: number): number {
  while (i < lines.length && isBlank(lines[i])) i += 1;
  return i;
}

function splitRow(line: string): string[] {
  const cells = line.split('|').map((cell) => cell.trim());
  if (cells[0] === '') cells.shift();
  if (cells[cells.length - 1] === '') cells.pop();
  return cells;
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function readTable(
  lines: string[],
  start: number,
): { next: number; block: LegalFlow } {
  const rows: string[][] = [];
  let i = start;
  while (i < lines.length && isTableRow(lines[i])) {
    const cells = splitRow(lines[i]);
    if (!isSeparatorRow(cells)) rows.push(cells);
    i += 1;
  }
  const [headers = [], ...body] = rows;
  return { next: i, block: { type: 'table', headers, rows: body } };
}

function readList(
  lines: string[],
  start: number,
): { next: number; block: LegalFlow } {
  const items: LegalInline[][] = [];
  let i = start;
  while (i < lines.length && isListItem(lines[i])) {
    items.push(parseInline(lines[i].slice(2)));
    i += 1;
  }
  return { next: i, block: { type: 'ul', items } };
}

function isParaLine(line: string): boolean {
  return (
    !isBlank(line) && !isHeading(line) && !isTableRow(line) && !isListItem(line)
  );
}

function readParagraph(
  lines: string[],
  start: number,
): { next: number; block: LegalFlow } {
  const raw: string[] = [];
  let i = start;
  while (i < lines.length && isParaLine(lines[i])) {
    raw.push(lines[i]);
    i += 1;
  }
  const hard = raw.some((line) => line.endsWith('\\'));
  if (!hard) {
    return {
      next: i,
      block: { type: 'p', children: parseInline(raw.join(' ')) },
    };
  }
  const children: LegalInline[] = [];
  raw.forEach((line, idx) => {
    if (idx > 0) children.push({ type: 'break' });
    const text = line.endsWith('\\') ? line.slice(0, -1) : line;
    children.push(...parseInline(text));
  });
  return { next: i, block: { type: 'p', children } };
}

function readFlow(
  lines: string[],
  i: number,
): { next: number; block: LegalFlow } {
  if (isTableRow(lines[i])) return readTable(lines, i);
  if (isListItem(lines[i])) return readList(lines, i);
  return readParagraph(lines, i);
}

export function parseLegalDocument(markdown: string): LegalDocument {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  let i = skipBlank(lines, 0);
  const titleLine = lines[i] ?? '';
  if (!titleLine.startsWith('# ')) {
    throw new Error('[exis] legal markdown needs an h1');
  }
  const title = displayTitle(titleLine.slice(2).trim());
  i = skipBlank(lines, i + 1);

  const updated = parseUpdatedLine(lines[i] ?? '');
  if (updated) i = skipBlank(lines, i + 1);

  const intro: LegalFlow[] = [];
  const sections: LegalSection[] = [];
  let current = intro;

  while (i < lines.length) {
    i = skipBlank(lines, i);
    if (i >= lines.length) break;
    if (lines[i].startsWith('## ')) {
      const section: LegalSection = {
        heading: parseInline(lines[i].slice(3).trim()),
        blocks: [],
      };
      sections.push(section);
      current = section.blocks;
      i += 1;
      continue;
    }
    const flow = readFlow(lines, i);
    current.push(flow.block);
    i = flow.next;
  }

  return {
    title,
    updatedText: updated?.text ?? '',
    updatedIso: updated?.iso ?? '',
    intro,
    sections,
  };
}
